import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import AssetUpload from "@/components/AssetUpload";
import { BRAND_KIT } from "@/constants/brandKitTestIds";
import {
  Field,
  TextInput,
  TextArea,
  NumberInput,
  BindingPillGroup,
  MultiSelectChips,
  FormActions,
} from "./Primitives";
import { _Header as Header, _Empty as Empty, _RowActions as RowActions } from "./LogosTab";
import useBindings from "@/lib/useBindings";
import useContentCategories from "@/lib/useContentCategories";

const PATH = "shapes";

const EMPTY = {
  name: "",
  image_url: null,
  description: "",
  binding_slugs: [],
  usable_in_category_slugs: [],
  priority: 100,
};

export default function ShapesTab({ brandId, canManage }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const { bindings, loading: bindingsLoading } = useBindings("shape", brandId);
  const { categories } = useContentCategories(brandId);
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.slug, label: c.name })),
    [categories],
  );

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/brands/${brandId}/brand-kit/${PATH}`);
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [brandId]);

  const openNew = () => {
    setForm({ ...EMPTY, priority: (items.length + 1) * 10 });
    setEditing("new"); setError("");
  };
  const openEdit = (it) => {
    setForm({
      name: it.name, image_url: it.image_url, description: it.description || "",
      binding_slugs: it.binding_slugs || [],
      usable_in_category_slugs: it.usable_in_category_slugs || [],
      priority: it.priority ?? 100,
    });
    setEditing(it.shape_id); setError("");
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      if (editing === "new") await api.post(`/brands/${brandId}/brand-kit/${PATH}`, form);
      else await api.put(`/brands/${brandId}/brand-kit/${PATH}/${editing}`, form);
      close(); await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save shape");
    } finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/brands/${brandId}/brand-kit/${PATH}/${id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not delete shape");
    }
  };

  if (loading) return <p className="text-sm text-[#6B6358]">Loading shapes…</p>;

  return (
    <div>
      <Header
        title="Shapes & graphic devices"
        subtitle="Brand-approved shapes, badges or supporting devices. Bindings come from the registry; lower priority wins when multiple shapes share a binding."
        canManage={canManage}
        actionTestId={BRAND_KIT.shapeAddButton}
        onAdd={openNew}
        active={editing !== null}
      />

      {editing !== null && (
        <form onSubmit={submit} className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6 mb-6">
          <h3 className="font-serif text-lg mb-4">
            {editing === "new" ? "New shape" : "Edit shape"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name">
              <TextInput value={form.name} onChange={(v) => setForm({ ...form, name: v })}
                testid={BRAND_KIT.shapeNameInput} required placeholder="e.g. Brand wave divider" />
            </Field>
            <div />
            <Field label="Shape file (image, SVG or PDF)" className="md:col-span-2">
              <AssetUpload
                value={form.image_url || ""}
                onChange={(url) => setForm({ ...form, image_url: url || null })}
                brandId={brandId}
                accept="image/*,application/pdf,.pdf,.svg"
                testIdSuffix="brandkit-shape"
              />
            </Field>
            <Field label="Description / usage notes (optional)" className="md:col-span-2">
              <TextArea value={form.description} onChange={(v) => setForm({ ...form, description: v })}
                testid={BRAND_KIT.shapeDescInput} rows={2} placeholder="When and where this shape may be used…" />
            </Field>
            <Field label="Layout bindings (where this shape is used)" className="md:col-span-2">
              <BindingPillGroup
                bindings={bindings}
                loading={bindingsLoading}
                selected={form.binding_slugs}
                onChange={(v) => setForm({ ...form, binding_slugs: v })}
                testidPrefix={BRAND_KIT.shapeBindingPrefix}
              />
            </Field>
          </div>

          <div className="border-t border-[#1A1F2C]/10 mt-6 pt-5">
            <div className="flex items-baseline justify-between mb-1">
              <h4 className="font-serif text-base">AI Assembly disambiguation</h4>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#B5462F]">advanced</span>
            </div>
            <p className="text-xs text-[#6B6358] mb-4 max-w-2xl">
              Used by the AI Assembly Agent when multiple shapes share a binding.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Priority (lower = picked first)">
                <NumberInput
                  value={form.priority}
                  onChange={(v) => setForm({ ...form, priority: v ?? 100 })}
                  testid={BRAND_KIT.shapePriorityInput}
                  step="1"
                  placeholder="100"
                />
              </Field>
              <div />
              <Field label="Restrict to content categories (optional)" className="md:col-span-2">
                <MultiSelectChips
                  options={categoryOptions}
                  selected={form.usable_in_category_slugs}
                  onChange={(v) => setForm({ ...form, usable_in_category_slugs: v })}
                  testidPrefix={BRAND_KIT.shapeCategoryPrefix}
                  emptyLabel="No content categories defined for this brand yet."
                />
              </Field>
            </div>
          </div>

          {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}
          <FormActions submitting={submitting} onCancel={close}
            submitTestId={BRAND_KIT.shapeSubmit} cancelTestId={BRAND_KIT.shapeCancel} />
        </form>
      )}

      {items.length === 0 ? (
        <Empty testid={BRAND_KIT.shapesEmpty} label="No shapes yet." />
      ) : (
        <div data-testid={BRAND_KIT.shapesList} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <div
              key={it.shape_id}
              data-testid={`${BRAND_KIT.shapeRowPrefix}${it.shape_id}`}
              className="border border-[#1A1F2C]/10 bg-white rounded-lg overflow-hidden"
            >
              <div className="aspect-square bg-[#F8F5EE] border-b border-[#1A1F2C]/10 flex items-center justify-center p-4">
                {it.image_url ? (
                  /\.pdf(\?|#|$)/i.test(it.image_url) ? (
                    <a
                      href={it.image_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex flex-col items-center justify-center text-center"
                    >
                      <span className="text-sm uppercase tracking-[0.22em] text-[#B5462F] font-medium">PDF</span>
                      <span className="text-[10px] text-[#6B6358] underline mt-2">Open</span>
                    </a>
                  ) : (
                    <img src={it.image_url} alt={it.name} className="max-w-full max-h-full object-contain" />
                  )
                ) : (
                  <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B6358]">No image</span>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-baseline justify-between gap-1">
                  <div className="font-serif text-base leading-tight">{it.name}</div>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358] shrink-0">P {it.priority ?? 100}</span>
                </div>
                {it.binding_slugs?.length > 0 && (
                  <div className="text-xs text-[#6B6358] mt-2">Bindings: {it.binding_slugs.join(", ")}</div>
                )}
                {it.usable_in_category_slugs?.length > 0 && (
                  <div className="text-[11px] text-[#6B6358] italic">Categories: {it.usable_in_category_slugs.join(", ")}</div>
                )}
                {it.description && (
                  <p className="text-xs text-[#3D3A35] mt-2 line-clamp-3">{it.description}</p>
                )}
                {canManage && (
                  <RowActions
                    onEdit={() => openEdit(it)}
                    onDelete={() => remove(it.shape_id)}
                    editTestId={`${BRAND_KIT.shapeEditPrefix}${it.shape_id}`}
                    deleteTestId={`${BRAND_KIT.shapeDeletePrefix}${it.shape_id}`}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
